import { useEffect, useMemo, useState } from "react";
import { Box, Chip, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, Close, KeyboardArrowDown } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { translate } from "../../../../../../translate/translate";
import { NpiHolidayService, Holiday } from "../../../../../../services/npi-holiday-service";
import { ErrorComponent } from "../../../../../../components/error/error.component";
import { toast } from "react-toastify";
import { MONTH_LABELS } from "./calendar-utils";
import NpiCalendarMonthGridComponent from "./npi-calendar-month-grid.component";
import NpiAddHolidayDialogComponent from "./npi-add-holiday-dialog.component";
import NpiHolidayMonthListComponent from "./npi-holiday-month-list.component";
import { useRef } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

type CalendarSelection = "BRASIL" | "CHINA" | "BOTH";
type ViewMode = "month" | "year";

const NpiCalendarComponent = () => {
    const holidayService = useMemo(() => new NpiHolidayService(), []);

    const today = new Date();
    const [selection, setSelection] = useState<CalendarSelection>("BOTH");
    const [viewMode, setViewMode] = useState<ViewMode>("month");
    const [year, setYear] = useState<number>(today.getFullYear());
    const [month, setMonth] = useState<number>(today.getMonth());
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addDialogCountry, setAddDialogCountry] = useState<"BRASIL" | "CHINA">("BRASIL");
    const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);
    const toggleActiveSx = { backgroundColor: "#003FFB", color: "#FFFFFF", "&:hover": { backgroundColor: "#003FFB" }, borderRadius: "10px" };
    const toggleInactiveSx = { backgroundColor: "#E9EDF7", color: "#8A94A6", border: "none", "&:hover": { backgroundColor: "#DEE3F2" }, borderRadius: "10px" };
    const [pickerOpen, setPickerOpen] = useState(false);
    const monthLabelRef = useRef<HTMLDivElement>(null);

    const handlePickerChange = (value: any) => {
        if (!value) {
            return;
        }
        const date = value as Date;
        setYear(date.getFullYear());
        setMonth(date.getMonth());
        setPickerOpen(false);
    };

    const fetchHolidays = () => {
        holidayService
            .readAll()
            .then((response) => {
                setHolidays(response.data);
            })
            .catch((error: any) => ErrorComponent(error));
    };

    useEffect(() => {
        fetchHolidays();
    }, []);

    const holidaysForYear = (targetYear: number) =>
        holidays.filter((holiday) => holiday.date.startsWith(String(targetYear)));
    const holidaysForMonth = (targetYear: number, targetMonth: number) =>
        holidays.filter((holiday) => {
            const [holidayYear, holidayMonth] = holiday.date.split("-").map(Number);
            return holidayYear === targetYear && holidayMonth === targetMonth + 1;
        });

    const brasilMonthHolidays = holidaysForMonth(year, month).filter((holiday) => holiday.country === "BRASIL");
    const chinaMonthHolidays = holidaysForMonth(year, month).filter((holiday) => holiday.country === "CHINA");

    const combinedMonthHolidays = [
        ...(selection !== "CHINA" ? brasilMonthHolidays : []),
        ...(selection !== "BRASIL" ? chinaMonthHolidays : []),
    ];

    const brasilYearHolidays = holidaysForYear(year).filter((holiday) => holiday.country === "BRASIL");
    const chinaYearHolidays = holidaysForYear(year).filter((holiday) => holiday.country === "CHINA");
    const combinedYearHolidays = [
        ...(selection !== "CHINA" ? brasilYearHolidays : []),
        ...(selection !== "BRASIL" ? chinaYearHolidays : []),
    ];

    const handlePrev = () => {
        if (viewMode === "year") {
            setYear((current) => current - 1);
            return;
        }
        if (month === 0) {
            setMonth(11);
            setYear((current) => current - 1);
        } else {
            setMonth((current) => current - 1);
        }
    };

    const handleNext = () => {
        if (viewMode === "year") {
            setYear((current) => current + 1);
            return;
        }
        if (month === 11) {
            setMonth(0);
            setYear((current) => current + 1);
        } else {
            setMonth((current) => current + 1);
        }
    };

    const handleOpenAddHoliday = (country: "BRASIL" | "CHINA") => {
        setAddDialogCountry(country);
        setAddDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (!deleteTarget) {
            return;
        }
        holidayService
            .delete(deleteTarget.id)
            .then(() => {
                toast.success(translate.t("successfully_deleted"));
                setDeleteTarget(null);
                fetchHolidays();
            })
            .catch((error: any) => {
                ErrorComponent(error);
            });
    };

    const activeCountry: "BRASIL" | "CHINA" = selection === "CHINA" ? "CHINA" : "BRASIL";

    const renderYearMonths = (country: "BRASIL" | "CHINA") => (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {MONTH_LABELS.map((_, monthIndex) => (
                <Grid size={selection === "BOTH" ? 12 : 4} key={monthIndex}>
                    <NpiCalendarMonthGridComponent
                        country={country}
                        year={year}
                        month={monthIndex}
                        holidays={holidaysForYear(year).filter(
                            (holiday) =>
                                holiday.country === country && Number(holiday.date.split("-")[1]) === monthIndex + 1,
                        )}
                        onAddHoliday={handleOpenAddHoliday}
                        compact
                    />
                </Grid>
            ))}
        </Grid>
    );

    const headerLabel = () => {
        if (viewMode === "month") {
            return `${MONTH_LABELS[month]} ${year}`;
        }
        if (selection === "BOTH") {
            return `${translate.t("annual_calendar")} ${year}`;
        }
        return `${activeCountry === "CHINA" ? "🇨🇳 CHINA" : "🇧🇷 Brasil"} – ${translate.t("annual_calendar")} ${year}`;
    };

    return (
        <section className="npi-calendar-page">
            <div className="floating-title" style={{ padding: "0 24px 0 15px" }}>
                <Typography variant="h5">{translate.t("calendars")}</Typography>
            </div>
            

            <Box sx={{ px: "15px", pt: "16px", pb: 2  }}>
                <Box sx={{ border: "1px solid #DCE5F2", borderRadius: "12px", p: 2, mb: 2, backgroundColor: "#FFFFFF", boxShadow: "0 2px 1px -1px #0003, 0 1px 1px 0 #00000024, 0 1px 3px 0 #0000001f" }}>
                    <Grid container spacing={4} alignItems="flex-start">
                        <Grid>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3236", mb: 1 }}>
                                {translate.t("select_calendar").toUpperCase()}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, backgroundColor: "#E9EDF7", borderRadius: "10px", px: 0.5 }}>
                                <Button sx={selection === "BRASIL" ? toggleActiveSx : toggleInactiveSx} onClick={() => setSelection("BRASIL")}>
                                    🇧🇷 {translate.t("brasil")}
                                </Button>
                                <Button sx={selection === "CHINA" ? toggleActiveSx : toggleInactiveSx} onClick={() => setSelection("CHINA")}>
                                    🇨🇳 {translate.t("china")}
                                </Button>
                                <Button sx={selection === "BOTH" ? toggleActiveSx : toggleInactiveSx} onClick={() => setSelection("BOTH")}>
                                    {translate.t("side_by_side")}
                                </Button>
                            </Box>
                        </Grid>
                        <Grid>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3236", mb: 1 }}>
                                {translate.t("view_mode").toUpperCase()}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center"}}>
                                <Box sx={{ display: "flex", gap: 1, backgroundColor: "#E9EDF7", borderRadius: "10px", px: 0.5, width: "fit-content"  }}>
                                    <Button sx={viewMode === "month" ? toggleActiveSx : toggleInactiveSx} onClick={() => setViewMode("month")}>
                                        {translate.t("month")}
                                    </Button>
                                    <Button sx={viewMode === "year" ? toggleActiveSx : toggleInactiveSx} onClick={() => setViewMode("year")}>
                                        {translate.t("year")}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid sx={{ flexGrow: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3236", mb: 1 }}>
                                {translate.t("legend").toUpperCase()}:
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {selection !== "CHINA" && (
                                    <>
                                        <Chip
                                            label={translate.t("national_holiday")}
                                            variant="outlined"
                                            sx={{ borderColor: "#E85D5D", color: "#C02929" }}
                                        />
                                        <Chip
                                            label={translate.t("manaus_holiday")}
                                            variant="outlined"
                                            sx={{ borderColor: "#3FBF9F", color: "#1F7A63", backgroundColor: "#E9FBF6", fontWeight: 700 }}
                                        />
                                        <Chip
                                            label={translate.t("sao_paulo_holiday")}
                                            variant="outlined"
                                            sx={{ borderColor: "#4D8AF0", color: "#2A5FC7", backgroundColor: "#EAF2FE", fontWeight: 700 }}
                                        />
                                    </>
                                )}
                                {selection === "CHINA" && (
                                    <Chip
                                        label={translate.t("holiday")}
                                        variant="outlined"
                                        sx={{ borderColor: "#E85D5D", color: "#C02929" }}
                                    />
                                )}
                                {selection !== "BRASIL" && (
                                    <Chip
                                        label={translate.t("tiaoxiu_legend")}
                                        variant="outlined"
                                        sx={{ borderColor: "#F0A93C", color: "#B4650B", backgroundColor: "#FEF6E8", fontWeight: 700 }}
                                    />
                                )}
                                <Chip label={translate.t("weekend")} sx={{ backgroundColor: "#dde6ef", color: "#797c82", fontWeight: 700 }} />
                                <Chip label={translate.t("normal_business_day")} variant="outlined" sx={{borderColor: "#7da2d8", color: "#707579", fontWeight: 600}} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ px: "15px", pt: "16px", pb: 2, backgroundColor: "#FFFFFF", boxShadow: "0 2px 1px -1px #0003, 0 1px 1px 0 #00000024, 0 1px 3px 0 #0000001f"  }}>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <IconButton
                            onClick={handlePrev}
                            sx={{ backgroundColor: "#FFFFFF", border: "1px solid #4D6FEA", color: "#4D6FEA", "&:hover": { backgroundColor: "#EEF1F9" }, borderRadius: "6px" }}>
                            <ChevronLeft />
                        </IconButton>
                        <Box
                            ref={monthLabelRef}
                            sx={{
                                flexGrow: 1, border: "1px solid #A9BCF5", borderRadius: "8px", py: 1,
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                                backgroundColor: "#FFFFFF", cursor: "pointer",
                            }}
                            onClick={() => setPickerOpen(true)}
    >
                            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                                {headerLabel()}
                            </Typography>
                            <KeyboardArrowDown fontSize="small" sx={{ color: "#6B7280" }} />
                        </Box>

                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                            <DatePicker
                                open={pickerOpen}
                                onClose={() => setPickerOpen(false)}
                                value={new Date(year, month, 1)}
                                onChange={handlePickerChange}
                                views={["year", "month"]}
                                openTo="year"
                                slotProps={{
                                    textField: { sx: { position: "absolute", width: 0, height: 0, padding: 0, border: "none", visibility: "hidden" } },
                                    popper: { anchorEl: monthLabelRef.current, placement: "bottom" },
                                }}
                            />
                        </LocalizationProvider>
                        <IconButton
                            onClick={handleNext}
                            sx={{ backgroundColor: "#FFFFFF", border: "1px solid #4D6FEA", color: "#4D6FEA", "&:hover": { backgroundColor: "#EEF1F9" }, borderRadius: "6px" }}>
                            <ChevronRight />
                        </IconButton>
                    </Box>

                    {viewMode === "month" ? (
                        selection === "BOTH" ? (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid size={6}>
                                    <NpiCalendarMonthGridComponent
                                        country="BRASIL"
                                        year={year}
                                        month={month}
                                        holidays={brasilMonthHolidays}
                                        onAddHoliday={handleOpenAddHoliday}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <NpiCalendarMonthGridComponent
                                        country="CHINA"
                                        year={year}
                                        month={month}
                                        holidays={chinaMonthHolidays}
                                        onAddHoliday={handleOpenAddHoliday}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Box sx={{ mb: 3 }}>
                                <NpiCalendarMonthGridComponent
                                    country={activeCountry}
                                    year={year}
                                    month={month}
                                    holidays={activeCountry === "BRASIL" ? brasilMonthHolidays : chinaMonthHolidays}
                                    onAddHoliday={handleOpenAddHoliday}
                                />
                            </Box>
                        )
                    ) : selection === "BOTH" ? (
                        <Grid container spacing={2}>
                            <Grid size={6}>{renderYearMonths("BRASIL")}</Grid>
                            <Grid size={6}>{renderYearMonths("CHINA")}</Grid>
                        </Grid>
                    ) : (
                        renderYearMonths(activeCountry)
                    )}

                    {viewMode === "month" ? (
                        <NpiHolidayMonthListComponent
                            month={month}
                            holidays={combinedMonthHolidays}
                            onDelete={setDeleteTarget}
                        />
                    ) : (
                        <NpiHolidayMonthListComponent
                            title={`${translate.t("holidays_and_events_of_year")} (${year})`}
                            holidays={combinedYearHolidays}
                            onDelete={setDeleteTarget}
                        />
                    )}

                    <NpiAddHolidayDialogComponent
                        open={addDialogOpen}
                        defaultCountry={addDialogCountry}
                        onClose={() => setAddDialogOpen(false)}
                        onSaved={fetchHolidays}
                    />

                    <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
                        <Grid p={2} container justifyContent="space-between" sx={{ backgroundColor: "primary.main" }}>
                            <Grid>
                                <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
                                    {translate.t("delete")}
                                </Typography>
                            </Grid>
                            <Grid sx={{ cursor: "pointer", color: "#ffffff" }}>
                                <Close onClick={() => setDeleteTarget(null)} />
                            </Grid>
                        </Grid>
                        <DialogContent>
                            <Typography>{translate.t("confirm_delete_holiday")}</Typography>
                            <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>{deleteTarget?.description}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Grid container justifyContent="flex-end" gap={1} p={2}>
                                <Button color="secondary" variant="outlined" onClick={() => setDeleteTarget(null)}>
                                    {translate.t("cancel")}
                                </Button>
                                <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                                    {translate.t("delete")}
                                </Button>
                            </Grid>
                        </DialogActions>
                        </Dialog>
                </Box>
            </Box>
        </section>
    );
};

export default NpiCalendarComponent;
